import * as React from "react";
import { useState } from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, MenuItem } from "@mui/material";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import { useSaveFieldAdminDeviceMutation, useSetFieldAdminDeviceStatusMutation } from "./fieldAdminApi";
import { AMBER, BORDER } from "../../Global/colors";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";
import { FAFieldError } from "./FAStates";
import { FATextField, FASelect } from "./StockFormShell";
import { FA_DIALOG_PAPER_SX } from "./FALayout";

const DEVICE_TYPES = ["Phone", "Tablet"];

// The device register's add/edit form and its decommission confirmation, plus the toast
// copy that exists solely for the form. Split out of a 441-line Devices.js.

export function DeviceDialog({ device, installers, onClose }) {
  const dispatch = useDispatch();
  const isEdit = Boolean(device.id);
  const [saveDevice, { isLoading, error }] = useSaveFieldAdminDeviceMutation();

  const [fleetId, setFleetId] = useState(device.fleet_id || "");
  const [assetId, setAssetId] = useState(device.asset_id || "");
  const [deviceType, setDeviceType] = useState(device.device_type || "Phone");
  const [installerId, setInstallerId] = useState(device.assigned_installer_id ?? "");
  const [deviceStatus, setDeviceStatus] = useState(device.status || "in_field");

  const disabled = !fleetId.trim() || isLoading;

  const handleSave = async () => {
    const result = await saveDevice({
      id: device.id || undefined,
      fleet_id: fleetId.trim(),
      asset_id: assetId.trim(),
      device_type: deviceType,
      status: deviceStatus,
      assigned_installer_id: deviceStatus === "decommissioned" || installerId === "" ? null : Number(installerId),
    });
    if (!result.error) {
      dispatch(showSuccess(deviceToastFor(device, deviceStatus === "decommissioned" ? null : installerId)));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth sx={[FA_DIALOG_PAPER_SX, { "& .MuiDialog-paper": { maxWidth: "460px" } }]}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <PhoneAndroidOutlinedIcon sx={{ color: AMBER, fontSize: "1.15rem" }} />
        <Box sx={{ flex: 1, fontSize: "1.05rem", fontWeight: 700 }}>{isEdit ? "Edit device" : "Add device"}</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.45)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: BORDER }}>
        <Typography sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "8px", p: "10px 12px", fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.5, mb: "16px" }}>
          <Box component="span" sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>IMS-issued kit.</Box> Devices are issued from IMS stock. Assign to an installer to put a device in the field, or leave unassigned to hold it as a spare on the van.
        </Typography>

        <Box sx={{ mb: "14px" }}>
          <FATextField label="Fleet ID" value={fleetId} onChange={(e) => setFleetId(e.target.value)} placeholder="e.g. Tjommie-fleet-0184" autoFocus />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mb: "14px" }}>
          <FATextField label="Asset ID" value={assetId} onChange={(e) => setAssetId(e.target.value)} placeholder="e.g. ID-04812" />
          <FASelect label="Type" value={deviceType} onChange={(e) => setDeviceType(e.target.value)}>
            {DEVICE_TYPES.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </FASelect>
        </Box>
        <Box sx={{ mb: "14px" }}>
          <FASelect
            label="Assigned installer"
            value={installerId}
            onChange={(e) => setInstallerId(e.target.value)}
            disabled={deviceStatus === "decommissioned"}
          >
            <MenuItem value="">Unassigned (spare)</MenuItem>
            {installers.map((installer) => (
              <MenuItem key={installer.id} value={installer.id}>{installer.name}</MenuItem>
            ))}
          </FASelect>
        </Box>
        {/* Only two statuses: a device is Spare by virtue of having no installer, so
            offering it here as a third option would let the two contradict. */}
        <FASelect label="Status" value={deviceStatus} onChange={(e) => setDeviceStatus(e.target.value)}>
          <MenuItem value="in_field">In field</MenuItem>
          <MenuItem value="decommissioned">Decommissioned</MenuItem>
        </FASelect>

        <FAFieldError error={error} />
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton onClick={handleSave} disabled={disabled}>
          {isLoading ? "Saving…" : "Save device"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

// devices.html toasts a distinct string per outcome rather than one generic "saved":
// re-issuing to an installer and returning to the spare pool read differently to the FA.
export function deviceToastFor(device, assignedInstallerId) {
  if (!device.id) return "Device added";
  const was = device.assigned_installer_id ?? null;
  const now = assignedInstallerId === "" || assignedInstallerId == null ? null : Number(assignedInstallerId);
  if (was !== null && now === null) return "Returned to spare pool";
  if (was === null && now !== null) return "Device re-issued";
  return "Device updated";
}

export function DecommissionConfirmDialog({ device, onClose }) {
  const dispatch = useDispatch();
  const [setStatus, { isLoading }] = useSetFieldAdminDeviceStatusMutation();

  const handleConfirm = async () => {
    const result = await setStatus({ device_id: device.id, status: "decommissioned" });
    if (!result.error) {
      dispatch(showSuccess("Device decommissioned"));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 700 }}>Decommission this device?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
          Decommissioning {device.fleet_id} returns it to IMS and unassigns any installer. It stays on record and can be re-issued. No hard delete.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton danger onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? "Decommissioning…" : "Decommission"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
